import { Injectable } from "@angular/core";
import { SuccessResult } from "../model/connect";
import { UserInfo } from "~/app/core/model/user-info";
import { UserLoginResult } from "~/app/core/model/user-login-result";
import { NetworkService } from "~/app/core/network.service";
import { StorageService } from "~/app/core/storage.service";
import { InitInfo } from "~/app/core/model/init-info";
import { InterfaceUserProfileService } from "~/app/core/user-profile-service/interface-user-profile-service";
import { SampleUserProfileService } from "~/app/core/user-profile-service/sample-user-profile-service";
import { HttpUserProfileService } from "~/app/core/user-profile-service/http-user-profile-service";
import { Settings } from "~/app/core/model/settings";
import { SettingsService } from "~/app/core/settings.service";
import * as Log from "~/app/core/log";
import { InteractionService } from "~/app/core/interaction.service";
import { RPCService } from "~/app/core/rpc.service";
import { LocationInfoRPC } from "~/app/core/model/rpc/location-info-rpc";
import { UserProperty } from "~/app/core/model/user-property";
import { DeviceSpecs } from "~/app/core/model/rpc/device-specs";
import { NotificationService } from "~/app/core/notification.service";

@Injectable()
export class UserProfileService {
    private readonly httpService: HttpUserProfileService;
    private readonly sampleService: SampleUserProfileService;

    private sample: boolean = null;
    private service: InterfaceUserProfileService;

    constructor(
        private readonly notify: NotificationService,
        private readonly networkService: NetworkService,
        private readonly storageService: StorageService,
        private readonly settingsService: SettingsService,
        private readonly interactionService: InteractionService,
        private readonly rpcService: RPCService
    ) {
        Log.debug(Log.Tag.UserProfileService, "CONSTRUCT USER PROFILE SERVICE");
        this.sampleService = new SampleUserProfileService(settingsService, storageService);
        this.httpService = new HttpUserProfileService(networkService, settingsService, storageService, rpcService);
        this.setUseSample(this.settingsService.loadSettings().useSampleUser);
        this.interactionService.updateUser(this.userInfo);
    }

    get userInfo(): UserInfo {
        return this.storageService.loadUserInfo(this.settingsService.loadSettings().env);
    }

    get deviceInfo(): InitInfo {
        return this.storageService.loadInitInfo();
    }

  setReferralCode(referralCode: string) {
    this.storageService.storeCode(referralCode);
  }
  getReferralCode(): string {
    return this.storageService.loadCode();
  }

    setUseSample(useSamples: boolean) {
        const changed = this.sample !== useSamples;
        if (changed) {
            const search = this.storageService.loadOfferSearch();
            const settings: Settings = this.settingsService.loadSettings();
            settings.useSampleUser = useSamples;
            this.settingsService.saveSettings(settings);
            if (this.sample != null) {
                this.storageService.resetData();
                this.storageService.resetLogin();
                if (search) {
                    this.storageService.storeOfferSearch(search);
                }
            }

            if (useSamples) {
                Log.debug(Log.Tag.UserProfileService, "Setup sample user profile service.");
                this.service = this.sampleService;
            } else {
                Log.debug(Log.Tag.UserProfileService, "Setup http user profile service.");
                this.service = this.httpService;
            }

            this.service.initService();
            this.sample = useSamples;
            this.interactionService.updateUser(this.userInfo);
        }
    }

    setWelcomed() {
        this.service.setWelcomed();
    }

    isWelcomed(): boolean {
        return this.service.isWelcomed();
    }

    captureLocation(location: LocationInfoRPC): void {
        const currentLocation = this.storageService.loadUserLocation();
        if (!location) {
            return;
        }
        if ((location.latitude && location.longitude) || (location.zip && !currentLocation.longitude && !currentLocation.latitude)) {
            Log.info(Log.Tag.UserProfileService, "CAPTURE LOCATION: " + JSON.stringify(location));
            this.service.captureLocation(location).then((result) => {
                Log.info(Log.Tag.UserProfileService, "SAVE CAPTURED LOCATION");
                this.storageService.storeUserLocation(location);
            });
        }
    }

    updateUserProperty(property: UserProperty, value: string): Promise<void> {
        return this.service.updateUserProperty(property, value);
    }

    testResetWelcomed(): void {
        this.service.testResetWelcomed();
    }

    testResetFirstRedeem(): void {
        this.service.testResetFirstRedeem();
    }

    showRegister(): void {
        this.service.showRegister();
    }

    testDeleteUser(): Promise<SuccessResult> {
        return this.service.testDeleteUser();
    }

    isAuthenticated(): boolean {
        return this.service.isAuthenticated();
    }

    needsValidation(): boolean {
        return this.service.needsValidation();
    }

    alreadyRedeemed(): boolean {
        const userInfo = this.userInfo;
        return userInfo && userInfo.authToken != null && userInfo.firstRedeemDate != null;
    }

    alreadyViewedShareInstructions(): boolean {
        const settings = this.settingsService.loadSettings();
        return !settings.showShareInstructions;
    }

    initDevice(): Promise<InitInfo> {
        const deviceSpecs: DeviceSpecs = {};
        return this.service.initDevice(deviceSpecs);
    }

     getUserReferralCode(): Promise<string>{
        return this.service.getUserReferralCode();
    }

    forgotPassword(email: string): Promise<SuccessResult> {
        return this.service.forgotPassword(email);
    }

    resetPassword(email: string, code: string, newPassword: string): Promise<UserLoginResult> {
        return this.updateUserResponse(this.service.resetPassword(email, code, newPassword));
    }

    register(firstName: string, lastName: string, email: string, password: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.notify.getPushToken().then((token) => {
                this.updateUserResponse(this.service.register(firstName, lastName, email, password, token)).then((result) => {
                    resolve(result);
                }, error => reject(error));
            }, error => reject(error));
        });
        return ;
    }

    resendValidationCode(email: string): Promise<SuccessResult> {
        return this.service.resendValidationCode(email);
    }

    login(email: string, password: string): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.notify.getPushToken().then((token) => {
                this.updateUserResponse(this.service.login(email, password, token)).then((result) => {
                    resolve(result);
                }, error => reject(error));
            }, error => reject(error));
        });
    }

    loginWithFacebook(): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.notify.getPushToken().then((token) => {
                this.updateUserResponse(this.service.loginWithFacebook(token)).then((result) => {
                    resolve(result);
                }, error => reject(error));
            }, error => reject(error));
        });
    }

    loginWithGoogle(): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.notify.getPushToken().then((token) => {
                this.updateUserResponse(this.service.loginWithGoogle(token)).then((result) => {
                    resolve(result);
                }, error => reject(error));
            }, error => reject(error));
        });
    }

    loginWithApple(): Promise<UserLoginResult> {
        return new Promise((resolve, reject) => {
            this.notify.getPushToken().then((token) => {
                this.updateUserResponse(this.service.loginWithApple(token)).then((result) => {
                    resolve(result);
                }, error => reject(error));
            }, error => reject(error));
        });
    }

    validate(validationCode: string): Promise<SuccessResult> {
        return this.updateUserResponse(this.service.validate(validationCode));
    }

    abortValidation(): void {
        return this.service.abortValidation();
    }

    logout(): void {
        this.service.logout();
        this.interactionService.updateUser(null);
    }

    setFirstRedemption(): UserInfo {
        return this.service.setFirstRedemption();
    }

    setFirstShare() {
        const settings = this.settingsService.loadSettings();
        if (settings.showShareInstructions) {
            settings.showShareInstructions = false;
            this.settingsService.saveSettings(settings);
        }
    }

    contactUs(reason: 'support'|'merchants'|'sponsors'|'disable',
              firstName: string, lastName: string, email: string, content: string): Promise<SuccessResult> {
        return this.service.contactUs(reason, firstName, lastName, email, content);
    }

    private updateUserResponse<T>(promise: Promise<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            promise.then((result) => {
                this.interactionService.updateUser(this.userInfo);
                resolve(result);
            }, (error) => {
                reject(error);
            });
        });
    }
}
